<script setup lang="ts">
import { computed, useAttrs, watchEffect } from 'vue'
import {
  useCurrencyInput,
  type CurrencyDisplay,
  type CurrencyInputOptions,
  type NumberRange
} from 'vue-currency-input'
import { type VTextField } from 'vuetify/components'

interface Props {
  /** Minimum allowed value */
  min?: NumberRange['min']
  /** Maximum allowed value */
  max?: NumberRange['max']
  /** Number of decimal places */
  precision?: CurrencyInputOptions['precision']
  /** How to display the currency symbol */
  currencyDisplay?: CurrencyDisplay
}

const {
  precision = 2,
  currencyDisplay = 'symbol' as CurrencyDisplay,
  min = undefined,
  max = undefined
} = defineProps<Props>()

// Gets emitted by currency input composable
// eslint-disable-next-line vue/no-unused-emit-declarations
defineEmits(['change'])

defineOptions({
  name: 'MoneyField'
})

const model = defineModel({
  validator: () => true
})

const currencyOptions = computed<CurrencyInputOptions>(() => ({
  currency: 'EUR',
  locale: 'fr-FR',
  precision,
  valueRange: { min, max },
  currencyDisplay
}))

const { inputRef, setValue, formattedValue, setOptions } = useCurrencyInput(
  // eslint-disable-next-line vue/no-ref-object-reactivity-loss
  currencyOptions.value
)

watchEffect(() => {
  setOptions(currencyOptions.value)
})

watchEffect(() => {
  setValue(model.value)
})

const attrs: VTextField['$props'] = useAttrs()

const slots = defineSlots<VTextField['$slots']>()
</script>

<template>
  <VTextField ref="inputRef" v-model="formattedValue" :aria-label="attrs.label">
    <template v-for="(slot, index) in Object.keys(slots)" :key="index" #[slot]>
      <slot :name="slot" />
    </template>
  </VTextField>
</template>
